import * as THREE from 'three';

export type VisualAudioEvent = 'footstep' | 'gunfire' | 'chest' | 'glider';

export interface VisualAudioData {
  x: number;
  z: number;
  type: VisualAudioEvent;
  intensity: number; // 0.0 to 1.0
}

export class FpsAudioVisualizer {
  private container: HTMLDivElement;
  private indicators: { el: HTMLDivElement; icon: HTMLDivElement; age: number; life: number }[] = [];
  
  constructor(mount: HTMLElement) {
    this.container = document.createElement('div');
    this.container.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      width: 320px;
      height: 320px;
      transform: translate(-50%, -50%);
      pointer-events: none;
      z-index: 20;
    `;
    mount.appendChild(this.container);
  }
  
  public addEvent(data: VisualAudioData, playerX: number, playerZ: number, playerYawRad: number) {
    const dx = data.x - playerX;
    const dz = data.z - playerZ;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist < 1 || dist > 60) return; // Out of range for visual audio usually
    
    let angleRad = Math.atan2(dx, dz) - playerYawRad;
    angleRad = ((angleRad % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const angleDeg = angleRad * (180 / Math.PI);
    
    const indicator = document.createElement('div');
    indicator.style.cssText = `
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      height: 100%;
      transform: translate(-50%, -50%) rotate(${angleDeg}deg);
      transform-origin: center center;
      transition: opacity 0.1s;
    `;
    
    // The actual arc element offset to the edge
    const arc = document.createElement('div');
    const color = data.type === 'chest' ? 'rgba(255, 215, 0, 0.8)' : 
                  data.type === 'gunfire' ? 'rgba(255, 50, 50, 0.8)' : 
                  data.type === 'glider' ? 'rgba(50, 200, 255, 0.8)' :
                  'rgba(255, 255, 255, 0.7)'; // Footstep
    
    arc.style.cssText = `
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 12px;
      background: ${color};
      border-radius: 50% 50% 0 0;
      box-shadow: 0 0 8px ${color};
      opacity: ${Math.max(0.3, data.intensity)};
    `;
    
    const icon = document.createElement('div');
    icon.style.cssText = `
      position: absolute;
      top: -24px;
      left: 50%;
      transform: translateX(-50%) rotate(${-angleDeg}deg);
      font-size: 16px;
      text-shadow: 0 2px 4px rgba(0,0,0,0.8);
      color: white;
    `;
    
    switch(data.type) {
      case 'footstep': icon.innerHTML = '👣'; break;
      case 'gunfire': icon.innerHTML = '💥'; break;
      case 'chest': icon.innerHTML = '✨'; break;
      case 'glider': icon.innerHTML = '🪂'; break;
    }
    
    indicator.appendChild(arc);
    indicator.appendChild(icon);
    this.container.appendChild(indicator);
    
    this.indicators.push({ el: indicator, icon: icon, age: 0, life: 1.5 });
  }
  
  public update(dt: number, playerYawRad: number) {
    for (let i = this.indicators.length - 1; i >= 0; i--) {
      const ind = this.indicators[i]!;
      ind.age += dt;
      
      // Update icon counter-rotation so it stays upright
      // We stored the initial rotation on the container, so we don't need to recalculate player relative unless we want it to track real-time.
      // For simple VFX, static is fine.
      
      if (ind.age >= ind.life) {
        ind.el.remove();
        this.indicators.splice(i, 1);
      } else {
        const fade = 1 - (ind.age / ind.life);
        ind.el.style.opacity = fade.toString();
      }
    }
  }
}
